import React, { useState, useRef, useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { Grid, Box, Alert } from '@mui/material';
import { useTheme } from '@emotion/react';
import AddIcon from '@mui/icons-material/Add';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';
import { tokens } from '../../theme';
import MuiDialog from '../../SystemDesign/MuiDialog';
import FormInput from '../../SystemDesign/FormInput';
import FormSelect from '../../SystemDesign/FormSelect';
import ColoredSelector from '../../SystemDesign/ColoredSelector';
import CardStepper from '../../SystemDesign/CardStepper';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useCreateEvaluationMutation, useGetLiteUsersQuery, useGetPosteQuery, useGetDisciplineByIdQuery, useGetRaterTypesQuery } from '../../services/SkillsManagement';
import { ADD_EVALUATION, ADD_EVALUATION_TO_EMPLOYEE } from '../../Reducers/Actions';
import useUploadEvaluationEvidences from '../../hooks/useUploadEvaluationEvidences';
import SkillSelector from '../../SystemDesign/SkillSelector';
import AddQuizEvaluation from '../Quiz_Evaluation/AddQuizEvaluation';
import { AuthContext } from '../../Context/AuthContext';
 

const validationSchema = Yup.object({
  employee_id: Yup.string().required('Employee is required'),
  evaluation_date: Yup.date().required('Evaluation date is required'),
  evaluation_type: Yup.string()
    .oneOf(['oral', 'written', 'practical', 'observation'], 'Invalid type')
    .required('Evaluation type is required'),
  rater_type: Yup.mixed().required('Rater type is required'),
});

const AddEvaluation = ({ open, onClose, onSubmit, presetRaterTypeName, allowedEvaluationTypes, allowedRaterTypeNames }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
    const {isEmployee} = useContext(AuthContext);

  const { setOpenSnackBar, setInfoAlert } = useContext(SnackBarContext);

  const [isFormValid, setIsFormValid] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  // Quiz modal state
  const [openQuiz, setOpenQuiz] = useState(false);
  const [isSelfAssessment, setIsSelfAssessment] = useState(false);
  const handleOpenQuiz = React.useCallback((posteId) => {
    if (!posteId) {
      // Do not use snackbar for this case; rely on inline warning alert rendered in the form
      return;
    }
    setOpenQuiz(true);
    onClose && onClose();
  }, [onClose]);

  const formRef = useRef(null);
  const [presetApplied, setPresetApplied] = useState(false);

  const [createEvaluation, { isLoading }] = useCreateEvaluationMutation();
  const { uploadForSkill } = useUploadEvaluationEvidences();
  const usersQueryArgs = React.useMemo(() => ({
    filters: '',
    sorting: '',
    pagination: JSON.stringify({ currentPage: 1, rowsPerPage: 50 }),
    type: '',
  }), []);
  const { data: usersData, isLoading: isLoadingUsers } = useGetLiteUsersQuery(usersQueryArgs, { refetchOnMountOrArgChange: false });
  const users = usersData?.users || [];

  // Best-effort: detect current user from usersData or localStorage
  const currentUser = React.useMemo(() => {
    const byFlag = users.find((u) => u.isCurrentUser || u.me || u.self);
    if (byFlag) return byFlag;
    try {
      const lsCandidates = ['currentUser', 'user', 'auth', 'profile'];
      for (const key of lsCandidates) {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const obj = JSON.parse(raw);
        const id = obj?.id ?? obj?.user?.id ?? obj?.data?.id;
        const email = obj?.email ?? obj?.user?.email ?? obj?.data?.email;
        const username = obj?.username ?? obj?.user?.username ?? obj?.data?.username;
        if (id) {
          const byId = users.find((u) => String(u.id) === String(id));
          if (byId) return byId;
        }
        if (email) {
          const byEmail = users.find((u) => String(u.email).toLowerCase() === String(email).toLowerCase());
          if (byEmail) return byEmail;
        }
        if (username) {
          const byName = users.find((u) => String(u.username).toLowerCase() === String(username).toLowerCase());
          if (byName) return byName;
        }
      }
    } catch (_) {
      // ignore parse errors
    }
    return undefined;
  }, [users]);

  const evaluationTypeOptions = React.useMemo(() => ([
    { id: 'oral', name: 'Oral', color: colors.green[400] },
    { id: 'written', name: 'Written', color: colors.blueAccent?.[400] || '#4AA3FF' },
    { id: 'practical', name: 'Practical', color: colors.Orange?.[400] || '#FF9800' },
  ]), [colors]);

  const evaluationTypeOptionsFiltered = React.useMemo(() => {
    if (Array.isArray(allowedEvaluationTypes) && allowedEvaluationTypes.length > 0) {
      const set = new Set(allowedEvaluationTypes.map(String));
      return evaluationTypeOptions.filter((o) => set.has(String(o.id)));
    }
    return evaluationTypeOptions;
  }, [evaluationTypeOptions, allowedEvaluationTypes]);

  // If only one evaluation type is allowed, preselect it for convenience
  React.useEffect(() => {
    const formik = formRef.current;
    if (!formik) return;
    const current = formik.values?.evaluation_type;
    if ((!current || String(current).trim() === '') && evaluationTypeOptionsFiltered.length === 1) {
      const only = evaluationTypeOptionsFiltered[0];
      if (only?.id) formik.setFieldValue('evaluation_type', String(only.id), false);
    }
  }, [evaluationTypeOptionsFiltered, formRef.current]);

  // Fetch rater types for dropdown (only when dialog is open to avoid duplicate/unused requests)
  const raterTypesArgs = React.useMemo(() => ({
    filters: '',
    sorting: '',
    pagination: JSON.stringify({ currentPage: 1, rowsPerPage: 100 }),
  }), []);
  const skipRaterTypes = !open;
  const { data: raterTypesResp, isLoading: loadingRaterTypes } = useGetRaterTypesQuery(
    raterTypesArgs,
    { skip: skipRaterTypes, refetchOnMountOrArgChange: false, refetchOnFocus: false, refetchOnReconnect: false }
  );
  // Map to ColoredSelector option shape
  const raterTypeOptions = (raterTypesResp?.data?.raterTypes || raterTypesResp?.raterTypes || []).map((rt, idx) => ({
    id: rt.id != null ? Number(rt.id) : undefined,
    name: rt.name,
    // Assign a soft color for variety; component doesn't require it but keeps parity with evaluation type field
    color: ['#9e9e9e', '#4AA3FF', '#26A69A', '#FF9800'][idx % 4],
  }));

  // Optional filtering of rater types by name (case-insensitive substring match)
  const raterTypeOptionsFiltered = React.useMemo(() => {
    if (Array.isArray(allowedRaterTypeNames) && allowedRaterTypeNames.length > 0) {
      const lowers = allowedRaterTypeNames.map((s) => String(s).toLowerCase());
      return raterTypeOptions.filter((o) => {
        const name = String(o?.name || '').toLowerCase();
        return lowers.some((needle) => name.includes(needle));
      });
    }
    return raterTypeOptions;
  }, [raterTypeOptions, allowedRaterTypeNames]);

  // If only one rater type is allowed, preselect it and apply self rules when applicable
  React.useEffect(() => {
    const formik = formRef.current;
    if (!formik) return;
    const current = formik.values?.rater_type;
    if ((!current || String(current).trim() === '') && raterTypeOptionsFiltered.length === 1) {
      const only = raterTypeOptionsFiltered[0];
      if (only?.id != null) {
        formik.setFieldValue('rater_type', String(only.id), false);
        const isSelf = String(only.name).toLowerCase().includes('self');
        setIsSelfAssessment(isSelf);
        if (isSelf && currentUser?.id != null) {
          formik.setFieldValue('employee_id', String(currentUser.id), false);
          setEmployeeId(String(currentUser.id));
        }
      }
    }
  }, [raterTypeOptionsFiltered, formRef.current, currentUser]);

  // If a preset rater type is requested (e.g., 'self'), prefill it and, if self, prefill employee with current user
  React.useEffect(() => {
    if (!open) return; // only when dialog open
    if (presetApplied) return;
    if (!presetRaterTypeName) return;
    if (!Array.isArray(raterTypeOptions) || raterTypeOptions.length === 0) return;
    const match = raterTypeOptions.find((o) => String(o.name).toLowerCase().includes(String(presetRaterTypeName).toLowerCase()));
    if (!match) return;
    const formik = formRef.current;
    if (!formik) return;
    // Set rater_type
    formik.setFieldValue('rater_type', match.id != null ? String(match.id) : '');
    // If self, set employee to current user when available
    const isSelf = String(match.name).toLowerCase().includes('self');
    setIsSelfAssessment(isSelf);
    if (isSelf && currentUser?.id != null) {
      formik.setFieldValue('employee_id', String(currentUser.id));
    }
    setPresetApplied(true);
  }, [open, presetApplied, presetRaterTypeName, raterTypeOptions, currentUser]);

  const submitForm = useCallback(() => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  }, []);

  // Level comes as string directly from UI (awareness | skilled_application | management)

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      setSubmitting(true);
      // Build one request per selected skill
      const empId = Number(values.employee_id);
      const evalDate = values.evaluation_date ? String(values.evaluation_date) : '';
      const expiryDate = values.expiry_date ? String(values.expiry_date) : '';
      const type = values.evaluation_type;

      if (!Number.isFinite(empId)) {
        setInfoAlert({ severity: 'error', message: 'Employee is required.' });
        setOpenSnackBar(true);
        setSubmitting(false);
        return;
      }

      if (!evalDate) {
        setInfoAlert({ severity: 'error', message: 'Evaluation date is required.' });
        setOpenSnackBar(true);
        setSubmitting(false);
        return;
      }

      if (!type) {
        setInfoAlert({ severity: 'error', message: 'Evaluation type is required.' });
        setOpenSnackBar(true);
        setSubmitting(false);
        return;
      }

      // Ensure rater type is selected and numeric
      const raterTypeId = Number(values.rater_type);
      if (!Number.isFinite(raterTypeId)) {
        setInfoAlert({ severity: 'error', message: 'Rater type is required.' });
        setOpenSnackBar(true);
        setSubmitting(false);
        return;
      }

      if (!selectedSkills || selectedSkills.length === 0) {
        setInfoAlert({ severity: 'error', message: 'Please select at least one skill to evaluate.' });
        setOpenSnackBar(true);
        setSubmitting(false);
        return;
      }

      const created = [];
      // Validate mapping for all selected skills before sending any request
      const unresolved = selectedSkills.filter((code) => {
        const mappedId = skillsIndexRef.current[code];
        return mappedId == null || !Number.isFinite(Number(mappedId));
      });
      if (unresolved.length > 0) {
        console.error('Missing skill_id mapping for codes:', unresolved);
        setInfoAlert({ severity: 'error', message: 'Some selected skills are not fully loaded yet. Please wait a moment and try again.' });
        setOpenSnackBar(true);
        setSubmitting(false);
        return;
      }
      for (const code of selectedSkills) {
        // Map code -> numeric skill_id if available
        const mappedId = skillsIndexRef.current[code];
        const skillIdNum = mappedId != null ? Number(mappedId) : Number(code);

        const levelStr = evaluationLevels[code];

        // Clamp score between 0 and 10
        const rawScore = evaluationScores[code];
        const numericScore = Number(rawScore === '' || rawScore == null ? 0 : rawScore);
        const clampedScore = Math.min(10, Math.max(0, numericScore));

        const payload = {
          employee_id: empId,
          skill_id: skillIdNum,
          evaluation_date: evalDate,
          expiry_date: expiryDate,
          score: clampedScore,
          comments: String(evaluationComments[code] ?? ''),
          evaluation_type: type,
          ...(levelStr ? { level: String(levelStr) } : {}),
          rater_type_id: raterTypeId,
        };

        if (!Number.isFinite(skillIdNum)) {
          console.warn('Skipping skill with non-numeric id for code', code);
          continue;
        }

        const result = await createEvaluation({ body: payload }).unwrap();
        const evaluationObj = result?.data || result?.evaluation || result;
        created.push(evaluationObj);
        dispatch({ type: ADD_EVALUATION, payload: evaluationObj });
        // Also attach to employee slice for real-time rendering in EvaluationList
        dispatch({ type: ADD_EVALUATION_TO_EMPLOYEE, payload: evaluationObj });

        // Upload evidence files for this skill (front-end collected files)
        const evalId = evaluationObj?.evaluation_id ?? evaluationObj?.id ?? evaluationObj?.evaluationId;
        const files = evaluationFiles?.[code] || [];
        if (evalId && Array.isArray(files) && files.length > 0) {
          await uploadForSkill({
            evaluationId: evalId,
            skillId: skillIdNum,
            files,
            level: evaluationLevels[code] ?? null,
            score: evaluationScores[code] ?? null,
            comment: evaluationComments[code] ?? '',
          });
        }
      }

      if (onSubmit) onSubmit(created);

      setInfoAlert({ severity: 'success', message: 'Evaluation(s) successfully created' });
      setOpenSnackBar(true);

      // Reset form and all skill-related state
      resetForm();
      setSelectedSkills([]);
      setEvaluationLevels({});
      setEvaluationScores({});
      setEvaluationComments({});
      setEvaluationFiles({});
      setEmployeeId('');
      setActiveStep(0);
      setCompleted({});
      
      onClose && onClose();
    } catch (error) {
      console.error('Error creating evaluation:', error);
      setInfoAlert({
        severity: 'error',
        message: error?.data?.message || 'Failed to create evaluation',
      });
      setOpenSnackBar(true);
    } finally {
      setSubmitting(false);
    }
  };

  const [formValues] = useState({
    employee_id: '',
    skill_id: '',
    evaluation_date: '',
    expiry_date: '',
    score: '',
    comments: '',
    evaluation_type: '',
    rater_type: '',
    poste_name: '',
    discipline_name: '',
  });

  // Stepper state
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState({});
  const steps = ['Evaluation Info', 'Select Skills'];

  // Validate only current step before navigating forward
  const touchFieldsForStep = (formik, step) => {
    let fields = [];
    switch (step) {
      case 0:
        fields = ['employee_id', 'evaluation_type', 'evaluation_date'];
        break;
      case 1:
        // No required fields enforced here (skills optional), keep empty
        fields = [];
        break;
      default:
        fields = [];
    }
    const touched = { ...formik.touched };
    fields.forEach((f) => {
      touched[f] = true;
    });
    formik.setTouched(touched, true);
  };

  const isStepValid = (values, errors, step) => {
    switch (step) {
      case 0:
        return !errors.employee_id && !errors.evaluation_type && !errors.evaluation_date;
      case 1:
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    const formik = formRef.current;
    if (!formik) return;
    if (activeStep === 0) {
      touchFieldsForStep(formik, 0);
      formik.validateForm().then((errors) => {
        if (isStepValid(formik.values, errors, 0)) {
          // Block progression if employee selected but no poste assigned
          const missingPoste = Boolean(formik.values?.employee_id) && !selectedPosteId;
          if (missingPoste) {
            // Inline alert is already visible; just do not proceed
            return;
          }
          // If written evaluation, open quiz instead of going to step 1
          if (String(formik.values.evaluation_type) === 'written') {
            handleOpenQuiz(selectedPosteId);
            return;
          }
          setActiveStep(1);
        } else {
          setInfoAlert({ severity: 'error', message: 'Please complete required fields.' });
          setOpenSnackBar(true);
        }
      });
    } else {
      setActiveStep((s) => Math.min(s + 1, steps.length - 1));
    }
  };

  const handleBack = () => setActiveStep((s) => Math.max(0, s - 1));

  const handleStepClick = (step) => {
    const formik = formRef.current;
    if (!formik) return setActiveStep(step);
    if (step > activeStep && activeStep === 0) {
      touchFieldsForStep(formik, 0);
      formik.validateForm().then((errors) => {
        if (isStepValid(formik.values, errors, 0)) {
          // Block progression if employee selected but no poste assigned
          const missingPoste = Boolean(formik.values?.employee_id) && !selectedPosteId;
          if (missingPoste) {
            return;
          }
          setActiveStep(step);
        } else {
          setInfoAlert({ severity: 'error', message: 'Please complete required fields.' });
          setOpenSnackBar(true);
        }
      });
    } else {
      setActiveStep(step);
    }
  };

  // Declarative field configuration for flexible layout and easy maintenance
  const formFields = React.useMemo(
    () => [
      { type: 'raterType', name: 'rater_type', label: 'Rater Type' },
      { type: 'selectEmployee', name: 'employee_id', label: 'Employee', required: true },
      { type: 'selectEvaluationType', name: 'evaluation_type', label: 'Evaluation Type', required: true },
      { type: 'readonly', name: 'poste_name', label: 'Position', required: true },
      { type: 'readonly', name: 'discipline_name', label: 'Discipline', required: true },
      { type: 'date', name: 'evaluation_date', label: 'Evaluation Date', required: true },
      { type: 'date', name: 'expiry_date', label: 'Expiry Date', required: true },
    //   { type: 'number', name: 'score', label: 'Score', required: true },
    ],
    []
  );

  function getFieldGridProps(field) {
    switch (field.name) {
      case 'employee_id':
        return { xs: 12, md: 6, lg: 4 };
      case 'rater_type':
      case 'poste_name':
      case 'discipline_name':
      case 'evaluation_type':
      case 'evaluation_date':
        return { xs: 12, md: 6, lg: 4 };
      case 'comments':
        return { xs: 12 };
      default:
        return { xs: 12, md: 6, lg: 6 };
    }
  }

  const dialogContent = () => (
    <Formik
      initialValues={formValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      innerRef={formRef}
      validateOnMount={true}
      enableReinitialize={false}
    >
      {(formikProps) => {
        const { isValid, dirty } = formikProps;
        if (formRef.current && (formRef.current.isValid !== isValid || formRef.current.dirty !== dirty)) {
          setTimeout(() => {
            setIsFormValid(isValid);
            setIsFormDirty(dirty);
          }, 0);
        }

        const selectedRater = raterTypeOptions.find((o) => String(o.id) === String(formikProps.values.rater_type));
        const raterIsSelf = selectedRater ? String(selectedRater.name).toLowerCase().includes('self') : false;
        const employeeOptions = raterIsSelf && currentUser ? [currentUser] : users;

        return (
          <Form id="add-evaluation-form">
            <CardStepper
              steps={steps}
              activeStep={activeStep}
              setActiveStep={handleStepClick}
              completed={completed}
              setCompleted={setCompleted}
              handleNext={handleNext}
              handleBack={handleBack}
              disableNext={activeStep === 0 && Boolean(formikProps.values.employee_id) && !selectedPosteId}
            >
              {activeStep === 0 && (
                <Box>
                  {Boolean(formikProps.values.employee_id) && !selectedPosteId && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      {raterIsSelf
                        ? 'You do not have a position assigned. Please assign a position to yourself or ask an administrator to assign one before continuing.'
                        : 'The selected employee does not have an assigned position. Please define a position for this employee first.'}
                    </Alert>
                  )}
                  <Grid container spacing={2}>
                    {formFields.map((field, index) => (
                      <Grid item size={{ ...getFieldGridProps(field) }} key={index}>
                        {field.type === 'selectEmployee' ? (
                          <ColoredSelector
                            options={employeeOptions}
                            value={formikProps.values.employee_id}
                            onChange={(value) => {
                              formikProps.setFieldValue('employee_id', value);
                              setEmployeeId(value);
                            }}
                            placeholder="Select an employee"
                            label={field.label}
                            valueKey="id"
                            labelKey="username"
                            searchable={true}
                            required={Boolean(field.required)}
                            error={formikProps.touched.employee_id && Boolean(formikProps.errors.employee_id)}
                            helperText={formikProps.touched.employee_id && formikProps.errors.employee_id}
                            disabled={isLoadingUsers || employeeOptions.length === 0 || raterIsSelf}
                            showColorDot={false}
                          />
                        ) : field.type === 'selectEvaluationType' ? (
                          <ColoredSelector
                            showColorDot={false}
                            options={evaluationTypeOptionsFiltered}
                            value={formikProps.values.evaluation_type}
                            onChange={(value) => {
                              formikProps.setFieldValue('evaluation_type', value);
                            }}
                            placeholder="Select evaluation type"
                            label={field.label}
                            colorKey="color"
                            valueKey="id"
                            labelKey="name"
                            searchable={false}
                            required={Boolean(field.required)}
                            error={formikProps.touched.evaluation_type && Boolean(formikProps.errors.evaluation_type)}
                            helperText={formikProps.touched.evaluation_type && formikProps.errors.evaluation_type}
                          />
                        ) : field.type === 'readonly' ? (
                          <FormInput
                            name={field.name}
                            label={field.label}
                            placeholder={field.placeholder || ''}
                            variant="outlined"
                            disabled
                          />
                        ) : field.type === 'raterType' ? (
                          <ColoredSelector
                            showColorDot={false}
                            options={raterTypeOptionsFiltered}
                            value={formikProps.values.rater_type}
                            onChange={(value) => {
                              formikProps.setFieldValue('rater_type', value);
                              const opt = raterTypeOptionsFiltered.find((o) => String(o.id) === String(value));
                              if (opt) setIsSelfAssessment(String(opt.name).toLowerCase().includes('self'));
                              if (opt && String(opt.name).toLowerCase().includes('self') && currentUser) {
                                formikProps.setFieldValue('employee_id', String(currentUser.id));
                                setEmployeeId(String(currentUser.id));
                              }
                            }}
                            placeholder="Select rater type"
                            label={field.label}
                            colorKey="color"
                            valueKey="id"
                            labelKey="name"
                            searchable={false}
                            // disabled={loadingRaterTypes || raterTypeOptions.length === 0}
                          />
                        ) : field.type === 'textarea' ? (
                          <FormInput
                            name={field.name}
                            label={field.label}
                            placeholder={field.placeholder || 'Additional notes (optional)'}
                            multiline
                            rows={field.rows || 3}
                            variant="outlined"
                          />
                        ) : field.type === 'date' ? (
                          <FormInput
                            name={field.name}
                            label={field.label}
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                            required={field.required}
                          />
                        ) : field.type === 'number' ? (
                          <FormInput
                            name={field.name}
                            label={field.label}
                            type="number"
                            placeholder={field.placeholder || '0 - 100'}
                            variant="outlined"
                            required={field.required}
                          />
                        ) : (
                          <FormInput
                            name={field.name}
                            label={field.label}
                            placeholder={field.placeholder || ''}
                            variant="outlined"
                          />
                        )}
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {activeStep === 1 && (
                <Box sx={{ 
                  minHeight: '45vh',
                  maxHeight: '55vh',
                  overflowY: 'auto',
                  mt: 2,
                  pr: 1
                }}>
                  <SkillSelector
                    unitName={undefined}
                    unitCode={undefined}
                    skills={posteSkills}
                    selectedSkills={selectedSkills}
                    evaluationLevels={evaluationLevels}
                    evaluationScores={evaluationScores}
                    evaluationComments={evaluationComments}
                    evaluationFiles={evaluationFiles}
                    onToggleSkill={handleToggleSkill}
                    onChangeLevel={handleChangeLevel}
                    onChangeScore={handleChangeScore}
                    onChangeComment={handleChangeComment}
                    onChangeFiles={handleChangeFiles}
                  />
                </Box>
              )}
            </CardStepper>
          </Form>
        );
      }}
    </Formik>
  );

  // Drive selection from local state to ensure reactivity on change
  const [employeeId, setEmployeeId] = useState('');
  // Keep local state in sync if formik gets a value programmatically
  React.useEffect(() => {
    const v = formRef.current?.values?.employee_id || '';
    // Only initialize when local state is empty
    if (!employeeId && v) setEmployeeId(v);
  }, [formRef.current?.values?.employee_id]);

  // Derive employee and poste data from selected employee (users already include Poste data)
  const selectedEmployee = React.useMemo(() => {
    return users.find((u) => String(u.id) === String(employeeId)) || null;
  }, [users, employeeId]);

  // Extract poste directly from employee data (no additional API call needed)
  const selectedPoste = React.useMemo(() => {
    return selectedEmployee?.Poste || null;
  }, [selectedEmployee]);

  const selectedPosteId = selectedPoste?.posteId ? String(selectedPoste.posteId) : undefined;

  // Extract quizId from poste
  const selectedQuizId = React.useMemo(() => {
    if (!selectedPoste) return undefined;
    const direct = selectedPoste.quizId ?? selectedPoste.quiz_id ?? selectedPoste.quiz?.id ?? selectedPoste.quiz?.quiz_id;
    return direct != null ? String(direct) : undefined;
  }, [selectedPoste]);

  // Extract discipline_id from poste
  const derivedDisciplineId = React.useMemo(() => {
    return selectedPoste?.discipline_id ? String(selectedPoste.discipline_id) : undefined;
  }, [selectedPoste]);

  // Only fetch discipline details if we have a discipline_id
  const { data: disciplineData } = useGetDisciplineByIdQuery(
    derivedDisciplineId ? { disciplineId: derivedDisciplineId } : { disciplineId: '' },
    { skip: !derivedDisciplineId, refetchOnMountOrArgChange: true }
  );

  // Single effect to update poste and discipline names when employee changes
  React.useEffect(() => {
    const formik = formRef.current;
    if (!formik) return;

    // Extract names
    const posteName = selectedPoste?.name || '';
    const disciplineName = disciplineData?.name || disciplineData?.discipline?.name || disciplineData?.data?.name || '';

    // Update both fields together to avoid multiple renders
    const updates = {};
    if (formik.values.poste_name !== posteName) {
      updates.poste_name = posteName;
    }
    if (formik.values.discipline_name !== disciplineName) {
      updates.discipline_name = disciplineName;
    }

    // Apply all updates at once
    if (Object.keys(updates).length > 0) {
      formik.setValues({ ...formik.values, ...updates }, false);
    }
  }, [selectedPoste, disciplineData]);

  // Per-poste skills: derive requirements from selected poste and render SkillSelector per unit
  // Keep an index of skills by code -> id to resolve skill_id at submit time
  const skillsIndexRef = useRef({});

  // Normalize requirements -> skills list (with minimal fields for selector)
  const posteSkills = React.useMemo(() => {
    // Extract skill requirements directly from selectedPoste
    const reqs = Array.isArray(selectedPoste?.skillRequirements) ? selectedPoste.skillRequirements : [];
    if (!Array.isArray(reqs) || reqs.length === 0) return [];
    
    return reqs
      .map((r) => {
        const s = r?.skill || r?.Skill || {};
        const id = s?.skill_id ?? r?.skill_id ?? s?.id;
        const name = s?.name ?? s?.skill_name ?? s?.skill ?? '';
        const description = s?.description || '';
        const unitId = s?.unit_id ?? s?.unitId;
        // Use numeric id as the unique selection key (stringified) instead of deriving a separate code
        const code = id != null ? String(id) : '';
        if (code) {
          // Maintain index for submit (code -> id)
          skillsIndexRef.current[code] = Number(id);
        }
        
        // Extract requirement-specific fields
        const requiredLevel = r?.required_level || '';
        const minScore = r?.min_score;
        const maxScore = r?.max_score;
        
        return {
          skillCode: code,
          skill: name || '',
          description,
          // Level descriptions for rendering beneath Level select
          awareness_description: s?.awareness_description || '',
          skilled_application_description: s?.skilled_application_description || '',
          management_description: s?.management_description || '',
          unit_id: unitId != null ? Number(unitId) : undefined,
          id,
          // Requirement fields for prefilling/hints
          required_level: requiredLevel,
          min_score: minScore,
          max_score: maxScore,
          raw: r,
        };
      });
  }, [selectedPoste]);

  // Clear selections when poste changes and prefill required levels
  React.useEffect(() => {
    if (!selectedPosteId) return;
    setSelectedSkills([]);
    
    // Prefill evaluation levels with required_level from skill requirements
    const defaultLevels = {};
    posteSkills.forEach((skill) => {
      if (skill.required_level) {
        // Map required_level to the correct format (lowercase with underscore)
        const levelMap = {
          'Awareness': 'awareness',
          'Skilled Application': 'skilled_application',
          'Management': 'management'
        };
        defaultLevels[skill.skillCode] = levelMap[skill.required_level] || skill.required_level.toLowerCase().replace(/ /g, '_');
      }
    });
    
    setEvaluationLevels(defaultLevels);
    setEvaluationScores({});
    setEvaluationComments({});
  }, [selectedPosteId, posteSkills]);
 React.useEffect(() => {
    if (!derivedDisciplineId) return;
    // Clear skill-related selections
    setSelectedSkills([]);
    setEvaluationLevels({});
    setEvaluationScores({});
    setEvaluationComments({});
  }, [derivedDisciplineId]);
  // We fetch skills inside UnitSkillBlock per unit

  // Local selection state for SkillSelector
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [evaluationLevels, setEvaluationLevels] = useState({});
  const [evaluationScores, setEvaluationScores] = useState({});
  const [evaluationComments, setEvaluationComments] = useState({});
  // Per-skill evidence files (front-only for now)
  const [evaluationFiles, setEvaluationFiles] = useState({}); // { [skillCode]: File[] }

  const handleToggleSkill = useCallback((code, checked) => {
    setSelectedSkills((prev) => (checked ? Array.from(new Set([...prev, code])) : prev.filter((c) => c !== code)));
    if (!checked) {
      setEvaluationLevels(({ [code]: _omit, ...rest }) => rest);
      setEvaluationScores(({ [code]: _omit, ...rest }) => rest);
      setEvaluationComments(({ [code]: _omit, ...rest }) => rest);
      setEvaluationFiles(({ [code]: _omit, ...rest }) => rest);
    }
  }, []);

  const handleChangeLevel = useCallback((code, level) => {
    setEvaluationLevels((prev) => ({ ...prev, [code]: level }));
  }, []);

  const handleChangeScore = useCallback((code, score) => {
    const n = Number(score);
    const clamped = Number.isFinite(n) ? Math.min(10, Math.max(0, n)) : '';
    setEvaluationScores((prev) => ({ ...prev, [code]: clamped }));
  }, []);

  const handleChangeComment = useCallback((code, comment) => {
    setEvaluationComments((prev) => ({ ...prev, [code]: comment }));
  }, []);

  const handleChangeFiles = useCallback((code, files) => {
    setEvaluationFiles((prev) => ({ ...prev, [code]: Array.isArray(files) ? files : [files] }));
  }, []);

  return (
    <>
      <MuiDialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        icon={<AddIcon />}
        title="Create New Evaluation"
        subtitle="Add a new evaluation to the system"
        content={dialogContent}
        existAction={activeStep === steps.length - 1}
        actionTitle="Create Evaluation"
        actionFunction={() => {
          if (activeStep !== steps.length - 1) {
            handleNext();
            return;
          }
          
          // On last step, submit the form
          submitForm();
        }}
        isLoading={isLoading}
        actionType="submit"
        formId="add-evaluation-form"
        disableAction={activeStep !== steps.length - 1}
        PaperProps={{
          sx: {
            minHeight: '80vh',
            maxHeight: '90vh'
          }
        }}
      />

      <AddQuizEvaluation
        open={openQuiz}
        onClose={() => setOpenQuiz(false)}
        posteId={selectedPosteId}
        quizId={selectedQuizId}
        isSelfAssessment={isSelfAssessment}
      />
    </>
  );
};

export default AddEvaluation;

